import { Component, useCallback, useMemo, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import type { InteractiveResult } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild } from '../store/selectors'
import { readSpec } from '../lib/specs'
import { useResolvedTheme } from '../hooks/useTheme'
import { useWakeLock } from '../hooks/useWakeLock'
import { play } from '../lib/feedback'
import { KidShell } from '../components/kid/KidShell'
import { Celebration } from '../components/kid/Celebration'

/**
 * Kid Mode — the only surface the child touches.
 *
 * SAFETY, stated plainly: nothing model-authored is executed here. The only
 * thing this view will render is a spec that `readSpec` has validated, and
 * what reaches the renderer is the value zod produced from that validation,
 * not the stored object. An artifact holding raw HTML — some older ones do —
 * therefore cannot run: it has no valid spec, so it falls through to the
 * refusal below. The guard is the `if (!read.ok)` return, not a convention.
 */
export function KidMode() {
  const artifact = useAuroraStore(
    (s: any) => (s.ui.selectedArtifactId ? s.artifacts[s.ui.selectedArtifactId] : undefined),
  )
  const blockRef = useAuroraStore((s: any) => s.ui.kidBlockRef as string | undefined)
  const child = useAuroraStore(activeChild)
  const theme = useResolvedTheme()

  const [attempt, setAttempt] = useState(0)
  const [finished, setFinished] = useState<InteractiveResult | null>(null)
  const [progress, setProgress] = useState<{ step?: number; steps?: number }>({})
  const [crashed, setCrashed] = useState(false)

  useWakeLock(true)

  const read = useMemo(() => readSpec(artifact), [artifact])

  const leave = useCallback(() => {
    // Back where she came from: a block sends the parent to the day, the
    // Studio sends them to the Studio.
    ;(auroraStore.getState() as any).exitKidMode(blockRef ? 'today' : 'studio')
  }, [blockRef])

  const onResult = useCallback(
    (result: InteractiveResult) => {
      const s = auroraStore.getState() as any
      if (child && artifact) {
        s.logInteractiveResult({
          childId: child._id,
          artifactId: artifact._id,
          result,
          blockRef,
        })
      }
      if (result.completed) setFinished(result)
      else leave()
    },
    [artifact, blockRef, child, leave],
  )

  const onProgress = useCallback((raw: unknown) => {
    // The renderers own their own progress shape and more will be added, so
    // read the two fields we can use and ignore anything else.
    const p = raw as Record<string, unknown> | null
    if (!p || typeof p !== 'object') return
    const step = numberOr(p.index ?? p.step ?? p.current)
    const steps = numberOr(p.total ?? p.steps ?? p.count)
    setProgress((prev) =>
      prev.step === step && prev.steps === steps ? prev : { step, steps },
    )
  }, [])

  const again = useCallback(() => {
    play('tap')
    setFinished(null)
    setProgress({})
    setCrashed(false)
    setAttempt((n) => n + 1)
  }, [])

  return (
    <KidShell
      title={artifact?.title}
      step={progress.step}
      steps={progress.steps}
      onExit={leave}
    >
      {!artifact ? (
        <Refusal
          title="Nothing to play yet"
          body="Pick something from the Studio and it will open here."
          onExit={leave}
        />
      ) : crashed ? (
        <Refusal
          title="This one stopped working"
          body="Nothing is lost. Try it again, or ask Aurora to rebuild it."
          onRetry={again}
          onExit={leave}
        />
      ) : !read.ok ? (
        /* THE GUARD. No spec, no play — whatever else the artifact holds. */
        <Refusal
          title="This one is not ready to play"
          body="It did not come back in a shape Aurora can run safely, so it stays closed. Asking for it again usually sorts it out."
          detail={read.errors}
          onExit={leave}
        />
      ) : finished ? (
        <Celebration
          title={child ? `You did it, ${child.name}` : 'You did it'}
          detail={detailFor(finished)}
          onAgain={again}
          onDone={leave}
        />
      ) : (
        <RendererBoundary key={attempt} onCrash={() => setCrashed(true)}>
          <InteractiveRenderer
            spec={read.spec}
            theme={theme}
            onResult={onResult}
            onProgress={onProgress}
          />
        </RendererBoundary>
      )}
    </KidShell>
  )
}

function numberOr(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * What she gets told at the end.
 *
 * Effort and completion, never a score — she is two, and "7 out of 10" is a
 * verdict. The parent reads the real numbers later, in the Story.
 */
function detailFor(result: InteractiveResult): string {
  const total = result.score?.total ?? 0
  if (total > 0) return `You worked through all ${total} of them.`
  const minutes = Math.round(result.durationMs / 60_000)
  return minutes >= 1 ? `You stayed with it for ${minutes} minutes.` : 'All the way to the end.'
}

/**
 * A dead end here is a dead end for a two-year-old, so every one of them says
 * what happened and keeps the way out in reach of the adult next to her.
 */
function Refusal({
  title,
  body,
  detail,
  onRetry,
  onExit,
}: {
  title: string
  body: string
  detail?: string[]
  onRetry?: () => void
  onExit: () => void
}) {
  return (
    <div className="kid-refusal">
      <p className="kid-refusal-title">{title}</p>
      <p className="kid-refusal-body">{body}</p>
      {detail && detail.length > 0 && (
        <details className="kid-refusal-detail">
          <summary>What went wrong</summary>
          <ul>
            {detail.slice(0, 5).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </details>
      )}
      <div className="kid-refusal-actions">
        {onRetry && (
          <button className="kid-btn" onClick={onRetry}>
            Try again
          </button>
        )}
        <button className="kid-btn kid-btn-quiet" onClick={onExit}>
          Leave Kid Mode
        </button>
      </div>
    </div>
  )
}

/**
 * A renderer that throws would leave a locked full-screen surface blank with a
 * child in front of it. This turns that into a message and a way out.
 */
class RendererBoundary extends Component<
  { children: ReactNode; onCrash: () => void },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[kid mode] renderer failed', error, info.componentStack)
    this.props.onCrash()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}
